import React, { Dispatch, useState } from "react";
import { IconButton, TextBox } from "@veneer/core";
import styled from "styled-components";
import { CartItem } from "../../../types/order";
import { skuLocalizationCodeMap } from "../../../shared/sku-localization";
import { useDispatch } from "react-redux";
import { RootActionTypes } from "redux/root-action";
import { useTranslation } from "react-i18next";
import { calculateTotalPerMonth, calculateTotalPerTerm } from "./shopping-cart-prices";
import { asyncUpdateCart } from "../../../redux/shopping-cart-thunks";
import { permissions } from "@mssi/pssp-shared";
import { useAuth } from "../../../redux/auth-hooks";

interface ShoppingCartMobileItemProps {
  orderItem: CartItem;
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 4px;
  padding: 7px;
`;

const ItemInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  min-width: 100%;
  width: 100%;
`;

const Width22 = styled.div`
  min-width: 22%;
  width: 22%;
`;

const Width10 = styled.div`
  min-width: 10%;
  width: 10%;
`;

const ItemInfo = styled.div`
  min-width: 68%;
  width: 68%;
`;

const Price = styled.span`
  font-size: 12px;
  font-weight: bold;
`;

const StyledTextBox = styled(TextBox)`
  max-width: 70px;
  margin-right: 10px;
  text-align: center;
`;
const ItemName = styled.div`
  font-weight: bold;
  font-size: 13px;
`;
const CartValue = styled.div`
  display: flex;
  flex-direction: row;
`;
const PerMonth = styled.div`
  font-size: 10px;
`;
const Border = styled.div`
  border: ${props => `1px solid ${props.theme.colors.border}`};
  margin-right: 5px;
  margin-left: 5px;
`;
const PerTerm = styled.div`
  font-size: 10px;
`;
const ShoppingCartMobileItem: React.FC<ShoppingCartMobileItemProps> = ({
  orderItem,
}) => {
  const dispatch = useDispatch<Dispatch<RootActionTypes>>();
  const { t } = useTranslation();
  const { authorizedFor } = useAuth();
  const totalPricePerMonth = calculateTotalPerMonth([orderItem]);
  const totalPricePerTerm = calculateTotalPerTerm([orderItem]);
  const canViewBundlePricing = authorizedFor([permissions.canViewBundlePricing]);

  const [qty, setQty] = useState<string>(`${orderItem.quantity || 0}`);

  return (
    <Wrapper>
      <ItemInfoWrapper>
        <ItemInfo>
          <ItemName>
            {orderItem.bundleName}
            {skuLocalizationCodeMap[orderItem.localizationCode] && (
              <>
                <br />
                {skuLocalizationCodeMap[orderItem.localizationCode]}
              </>
            )}
          </ItemName>
          {canViewBundlePricing && (
            <CartValue>
              <PerMonth>
                <div>{t("shoppingcart.totalpermonth")}</div>
                <Price>{totalPricePerMonth}</Price>
              </PerMonth>
              <Border />
              <PerTerm>
                <div>{t("shoppingcart.totalperterm")}</div>
                <Price>
                  {totalPricePerTerm}
                </Price>
              </PerTerm>
            </CartValue>
          )}
        </ItemInfo>

        <Width22>
          <StyledTextBox
            type="number"
            name="qty"
            min={1}
            max={99999}
            value={qty}
            onChange={(quantity: string) => {
              setQty(quantity);
            }}
            onBlur={() => {
              let quantityParsed = parseInt(qty, 10);

              if (Number.isNaN(quantityParsed)) {
                quantityParsed = 0;
              }

              dispatch(asyncUpdateCart(orderItem.bundleId, orderItem.localizationCode, quantityParsed, orderItem.solutionId) as any);
            }}
          />
        </Width22>

        <Width10>
          <IconButton
            onClick={() => {
              setQty("0");
              dispatch(asyncUpdateCart(orderItem.bundleId, orderItem.localizationCode, 0, orderItem.solutionId) as any);
            }}
            icon="close"
            className="vn-color--red"
          />
        </Width10>
      </ItemInfoWrapper>
    </Wrapper>
  );
};

export default ShoppingCartMobileItem;
